import { Injectable } from '@angular/core';
import { EventBusService } from './event-bus.service';
import { AppServiceHelperService } from './app-service-helper.service';
import { HttpClient } from '@angular/common/http';
import { map, take } from 'rxjs/operators';

/**
 * Role assignment
 */
export class RoleAssignment {
  /**
   * OID
   */
  public oid: number;

  /**
   * Role ID
   */
  public roleId: number;

  /**
   * Role description
   */
  public roleDescription: string;

  /**
   * User ID
   */
  public userId: number;

  /**
   * User description
   */
  public userDescription: string;

  /**
   * Project OID (always 0)
   */
  public ProjectstdId: number;

  /**
   * Localized role description
   */
  public roleDescriptionLocalized: string;
}

/**
 * Storage service that keeps app state
 */
@Injectable({
  providedIn: 'root'
})
export class AppStorageService {
  /**
   * The active VE
   */
  private valuationPortfolio: any = null;

  /**
   * The current user's role assignments
   */
  private roleAssignmentsCurrentUser: RoleAssignment[] = [];

  /**
   * The current user's details
   */
  private userDetails: any = null;

  /**
   * The selected VE
   */
  private selectedPortfolio: any = null;

  /**
   * The selected project
   */
  private selectedProject: any = null;

  /**
   * Constructor
   * @param eventBus EventBusService
   * @param appServiceHelper AppServiceHelperService
   * @param http HttpClient
   */
  constructor(private eventBus: EventBusService,
    private appServiceHelper: AppServiceHelperService,
    private http: HttpClient) { }


  /**
   * Sets the current valuation portfolio.
   * @param portfolio The portfolio (Valuation Event) object
   */
  public setActiveValuationPortfolio(portfolio: any) {
    this.valuationPortfolio = portfolio;
  }

  /**
   * Gets the current valuation portfolio
   * @returns The current valuation portfolio
   */
  public getActiveValuationPortfolio(): any {
    return this.valuationPortfolio;
  }

  /**
   * Reloads all role assignments
   * @returns A promise to wait for
   */
  public reloadRoleAssignments(): Promise<void> {
    this.roleAssignmentsCurrentUser = [];
    return this.roleAssignmentsQuery();
  }

  /**
   * Queries the role assignments
   * @returns
   */
  private roleAssignmentsQuery(): Promise<void> {
    return new Promise((resolve, _reject) => {
      const urlCurrentUser = this.appServiceHelper.getServiceEndpoint('pollux.settings.api', 'user/roleAssignments?useCurrentUser=true');

        this.http.get(urlCurrentUser).pipe(take(1)).pipe(map((resultCurrentUser) => {
        return {
          currentUser: resultCurrentUser as RoleAssignment[]
        }
      })).subscribe((result) => {
        this.roleAssignmentsCurrentUser = [];
        for (const element of result.currentUser) {
          this.roleAssignmentsCurrentUser.push(element);
        }
        resolve();
      });
    });
  }

  /**
   * Checks if the user has a specific role or not
   * @param roles List of roles
   * @returns true if the user has any of the given roles
   */
  public userIsInRole(roles: string[]): boolean {
    const userRoles = this.roleAssignmentsCurrentUser;
    if (userRoles?.length) {
      //check "virtual" "HasRole" role => if user has at least one role (any)
      if (roles.length === 1 && roles[0] === 'HasRole') {
        return userRoles.length > 0;
      }

      if (roles.some(role => ",Client Account,Geo Lead,Sector Lead,Project Team,Valuer,".indexOf(role) >= 0)) {
        if (userRoles.some(role => role.roleDescription == "_tmp all roles")) {
          return true;
        }
      }

      for (const role of userRoles) {
        if (roles.indexOf(role.roleDescription) >= 0) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Loads the user details for the current user
   * @returns A Promise to wait for
   */
  public loadCurrentUserDetails(): Promise<any> {
    return new Promise<any>((resolve, _reject) => {
      const url = this.appServiceHelper.getServiceEndpoint('pollux.api', 'user/current');

      this.http.get(url).pipe(take(1)).subscribe((result: any) => {
        this.userDetails = result;
        resolve(this.userDetails);
      });
    });
  }

  /**
   * Gets the user details for the current user
   * @returns The user details for the current user
   */
  public getCurrentUserDetails(): any {
    return this.userDetails;
  }

  /**
   * Sets the selected valuation portfolio. Used to synchronize the selection across the areas.
   * @param item The selected valuation event
   */
  public setSelectedPortfolio(item: any) {
    this.selectedPortfolio = item;
    this.eventBus.broadcastEvent('appStorage:setSelectedPortfolio', item);
  }

  /**
   * Gets the selected valuation portfolio. Used to synchronize the selection across the areas.
   * @returns The selected valuation portfolio
   */
  public getSelectedPortfolio(): any {
    return this.selectedPortfolio;
  }

  /**
   * Sets the selected project. Used to synchronize the selection across the areas.
   * @param item The selected project
   */
  public setSelectedProject(item: any): void {
    this.selectedProject = item;
    this.eventBus.broadcastEvent('appStorage:setSelectedProject', item);
  }

  /**
   * Gets the selected project. Used to synchronize the selection across the areas.
   * @returns The selected project
   */
  public getSelectedProject(): any {
    return this.selectedProject;
  }
}
